import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
/* import { NewLandingReviewsSection } from '@/pages/public/new/NewLandingReviewsSection'; */
import { GoogleReviewsSection } from './google-reviews/GoogleReviewsSection';

type ReclameAquiVerifiedSectionProps = {
	variant?: 'default' | 'newLanding' | 'embedded';
};

const RA_VERIFIED_BUNDLE_SRC =
	'https://s3.amazonaws.com/raichu-beta/ra-verified/bundle.js';
const RA_VERIFIED_DATA_ID = 'aDB0MmVDSVdiVU8wR0ZTejpwbHVtYWE=';

const RA_VERIFIED_MODEL = 'horizontal_1' as const;

function loadRaVerifiedEmbed(
	targetId: string,
	model: typeof RA_VERIFIED_MODEL,
) {
	return new Promise<void>((resolve, reject) => {
		const target = document.getElementById(targetId);
		if (!target) {
			reject(new Error(`RA target not found: ${targetId}`));
			return;
		}

		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.id = 'ra-embed-verified-seal';
		script.src = RA_VERIFIED_BUNDLE_SRC;
		script.setAttribute('data-id', RA_VERIFIED_DATA_ID);
		script.setAttribute('data-target', targetId);
		script.setAttribute('data-model', model);
		script.onload = () => resolve();
		script.onerror = () => reject(new Error(`RA bundle failed: ${model}`));

		target.appendChild(script);
	});
}

const ReclameAquiVerifiedSection = ({
	variant = 'default',
}: ReclameAquiVerifiedSectionProps) => {
	const [raSealSize, setRaSealSize] = useState<{
		width: number;
		height: number;
	} | null>(null);

	useEffect(() => {
		const targetId = `ra-verified-seal-${RA_VERIFIED_MODEL}`;
		const target = document.getElementById(targetId);
		if (!target) return;

		const observer = new ResizeObserver((entries) => {
			const { width, height } = entries[0]?.contentRect ?? {};
			if (width > 0 && height > 0) {
				setRaSealSize({
					width: Math.round(width),
					height: Math.round(height),
				});
			}
		});
		observer.observe(target);

		void loadRaVerifiedEmbed(targetId, RA_VERIFIED_MODEL).catch(() => {
			/* embed opcional */
		});

		return () => {
			observer.disconnect();
			target.replaceChildren();
			setRaSealSize(null);
		};
	}, []);

	const seals = (
		<div className="flex max-w-[100vw] flex-wrap items-center justify-center gap-5 md:gap-8">
			<div id="ra-verified-seal-horizontal_1" className="shrink-0" />
			<GoogleReviewsSection
				sealSize={raSealSize}
				variant={variant === 'default' ? 'default' : 'newLanding'}
			/>
		</div>
	);

	if (variant === 'embedded') {
		return (
			<div aria-label="Selos de confiança" className="w-full">
				{seals}
			</div>
		);
	}

	return (
		<section
			aria-label="Selos de confiança"
			className={cn(
				'w-full',
				variant === 'newLanding'
					? 'border-y border-brand-orange/10 bg-[#fffcfb] py-10 md:py-14'
					: 'border-t border-[#e4e4e0] bg-white py-5 md:py-6',
			)}
		>
			{variant === 'newLanding' ? (
				<div className="mx-auto max-w-6xl space-y-10 px-6">
					{seals}
				</div>
			) : (
				<div className="mx-auto flex max-w-[100vw] flex-wrap items-center justify-center gap-5 px-4 md:gap-8 md:px-6">
					<div
						id="ra-verified-seal-horizontal_1"
						className="shrink-0"
					/>
					<GoogleReviewsSection sealSize={raSealSize} />
				</div>
			)}
		</section>
	);
};

export { ReclameAquiVerifiedSection };
